import React from 'react';

import { Translate } from 'src/components/translate';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { Direction } from '@prisjakt/ui';

import * as S from './styles';

type SortInfo = {
  name: string;
  direction: 'asc' | 'desc';
};

interface ProductListingHeaderProps {
  selectedProperties: {
    id: string;
    name: string;
  }[];
  onSortChange: (key: string) => void;
  sortInfo: any;
  firstColumnIndent: number;
}

const truncate = (maxLength: number, text: string) =>
  text.length > maxLength ? `${text.substr(0, maxLength)}…` : text;

const getSortIcon = (sortInfo: SortInfo, name: string | boolean) =>
  sortInfo && sortInfo.name === name && sortInfo.direction === 'asc' ? (
    <S.SortAscIcon size={16} />
  ) : (
    <S.SortDescIcon size={16} />
  );

const isPriceSortMode = (sortInfo: SortInfo) => /^price/.test(sortInfo.name);
const getPriceSortMode = (sortInfo: SortInfo) =>
  isPriceSortMode(sortInfo) && sortInfo.name;
const getPriceColumnLabel = (sortInfo: SortInfo) => {
  const defaultLabel = (
    <Translate domain="category">product-list.price-column-heading</Translate>
  );
  switch (getPriceSortMode(sortInfo)) {
    case 'price':
      return defaultLabel;
    case 'price_include_shipping':
      return (
        <Translate domain="category">
          product-list.price-shipping-column-heading
        </Translate>
      );
    default:
      return defaultLabel;
  }
};
const getWidth = (numProperties: number) => {
  if (numProperties === 2) return 160;
  if (numProperties === 3) return 120;
  if (numProperties > 4) return 100;
  return undefined;
};

const createRange = (range: number) => [...Array(range).keys()];

const ProductListingHeader: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ProductListingHeaderProps>>
> = ({ selectedProperties, onSortChange, sortInfo, firstColumnIndent }) => (
  <S.ProductListingHeaderRoot>
    {firstColumnIndent > 0 &&
      createRange(firstColumnIndent).map(i => (
        <S.ProductListingHeaderCell key={`indent_${i}`} />
      ))}
    <S.ProductListingHeaderCell
      key="name"
      selected={sortInfo.name === 'name'}
      role="link"
      tabIndex={0}
      onClick={() => onSortChange('name')}
    >
      <S.ProductListingHeaderCellContent>
        <Translate domain="category">
          product-list.name-column-heading
        </Translate>
        {getSortIcon(sortInfo, 'name')}
      </S.ProductListingHeaderCellContent>
    </S.ProductListingHeaderCell>
    <S.ProductListingHeaderCell
      key="price"
      selected={sortInfo.name === getPriceSortMode(sortInfo)}
      role="link"
      tabIndex={0}
      onClick={() => onSortChange(getPriceSortMode(sortInfo) || 'price')}
    >
      <S.ProductListingHeaderCellContent right>
        {getPriceColumnLabel(sortInfo)}
        {getSortIcon(sortInfo, getPriceSortMode(sortInfo))}
      </S.ProductListingHeaderCellContent>
    </S.ProductListingHeaderCell>
    {selectedProperties?.map(prop => {
      const propertyName = `property:${prop.id}`;
      const isPropertySelected = sortInfo.name === propertyName;
      const shouldTruncateName =
        selectedProperties.length > 2 && prop.name !== truncate(20, prop.name);
      return (
        <S.ProductListingHeaderCell
          key={prop.id}
          width={getWidth(selectedProperties.length)}
          selected={isPropertySelected}
          aria-label={shouldTruncateName ? prop.name : undefined}
          role="link"
          tabIndex={0}
          onClick={() => onSortChange(propertyName)}
        >
          {shouldTruncateName ? (
            <Tooltip
              placement={Direction.Left}
              renderOnMouseOver
              title={prop.name}
            >
              <S.ProductListingHeaderCellContent>
                {truncate(20, prop.name)}
                {getSortIcon(sortInfo, propertyName)}
              </S.ProductListingHeaderCellContent>
            </Tooltip>
          ) : (
            <S.ProductListingHeaderCellContent>
              {prop.name}
              {getSortIcon(sortInfo, propertyName)}
            </S.ProductListingHeaderCellContent>
          )}
        </S.ProductListingHeaderCell>
      );
    })}
  </S.ProductListingHeaderRoot>
);

export default ProductListingHeader;
